import React, { useEffect, useState } from "react";
import FormSelect from "../common/FormSelect";
import { DropDownBold } from "../AdminIcons";
import {
  useGetRolesListQuery,
  useGetStaffWithNoRolesQuery,
  useAssignRoleMutation,
} from "../../services/user/userApi";
import { getApiErrorMessage, showToast } from "../../utils/toast";

const AssignRoleModal = ({ isOpen, onClose }) => {
  const { data, isLoading } = useGetRolesListQuery();
  const { data: staffWithNoRolesData, isLoading: isStaffWithNoRolesLoading } =
    useGetStaffWithNoRolesQuery();
  const [assignRole, { isLoading: isAssigning }] = useAssignRoleMutation();

  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState("");

  const rolesOptions = (data?.data || []).map((role) => ({
    label: role.name,
    value: role.id,
  }));

  const staffEmailOptions = (staffWithNoRolesData?.data || []).map((staff) => ({
    label: staff.email,
    value: staff.email,
  }));

  const closeModal = () => {
    setSelectedRole(null);
    setSelectedEmail("");
    onClose();
  };

  const handleBackdropClick = () => {
    closeModal();
  };

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  const handleAssignRole = async () => {
    if (!selectedEmail || !selectedRole) {
      return;
    }

    try {
      await assignRole({
        email: selectedEmail,
        roleId: selectedRole.id,
        roleName: selectedRole.name,
      }).unwrap();
      showToast({
        type: "success",
        message: "Role assigned successfully.",
      });
      closeModal();
    } catch (error) {
      showToast({
        type: "error",
        message: getApiErrorMessage(error, "Failed to assign role."),
      });
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center overflow-hidden bg-black/45 p-6 sm:p-10"
      onClick={handleBackdropClick}
    >
      <div
        className="max-h-[min(94vh,calc(100dvh-1.5rem))] w-full max-w-[933px] overflow-y-auto rounded-2xl border border-[#00000014] bg-[#EAE9E9] [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="px-4 pb-5 pt-6 sm:px-6 sm:pb-7 sm:pt-8 lg:px-12">
          <h3 className="text-lg font-medium leading-6 text-black sm:text-xl">Assign Role to User</h3>

          <div className="mt-6 space-y-6 sm:mt-10 sm:space-y-10">
            <div>
              <label className="mb-1.5 block  text-black">Select Role</label>
              <FormSelect
                id="assign-role-select"
                options={rolesOptions}
                defaultValue=""
                placeholder={isLoading ? "Loading..." : "Select Role"}
                controlWrapperClassName="mt-0"
                selectClassName="h-11 rounded-[5px] border border-[#00000014] bg-[#D9D9D961] px-4 text-base text-black sm:text-xl"
                dropdownClassName="left-0 max-h-[45vh] w-full overflow-y-auto px-3 py-2"
                icon={DropDownBold}
                iconProps={{ color: "#3C3C3C" }}
                onChange={(value) => {
                  const role = (data?.data || []).find((r) => r.id === value);
                  setSelectedRole(role);
                }}
              />
            </div>

            <div>
              <label className="mb-1.5 block  text-black">Email Address</label>
              <FormSelect
                id="assign-role-email"
                options={staffEmailOptions}
                defaultValue=""
                placeholder={isStaffWithNoRolesLoading ? "Loading..." : "Select Email"}
                controlWrapperClassName="mt-0"
                selectClassName="h-11 rounded-[5px] border border-[#00000014] bg-[#D9D9D961] px-4 text-base text-black sm:text-xl"
                dropdownClassName="left-0 max-h-[45vh] w-full overflow-y-auto px-3 py-2"
                icon={DropDownBold}
                iconProps={{ color: "#3C3C3C" }}
                onChange={(value) => setSelectedEmail(value)}
              />
            </div>
          </div>

          <div className="mt-8 flex flex-col-reverse gap-3 sm:mt-10 sm:flex-row sm:justify-end sm:gap-5">
            <button
              type="button"
              className="h-10 rounded-lg bg-[#DBDBDB] px-6 text-base font-medium text-[#686868] sm:h-8 sm:text-lg"
              onClick={closeModal}
            >
              Cancel
            </button>
            <button
              type="button"
              className="h-10 rounded-lg bg-[#923D8C] px-10 text-base font-medium text-[#FAFCFE] disabled:cursor-not-allowed disabled:opacity-70 sm:h-8 sm:text-lg"
              onClick={handleAssignRole}
              disabled={isAssigning || !selectedEmail || !selectedRole}
            >
              {isAssigning ? "Assigning..." : "Assign"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignRoleModal;
